import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Grid,
  Alert
} from '@mui/material';
import {
  Save as SaveIcon,
  CheckCircle as CompleteIcon,
  ArrowBack as BackIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import usePageTitle from '../../utils/usePageTitle';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  IconButton,
  Collapse
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const EffortDetail = () => {
  const { projectCode } = useParams();
  usePageTitle(`Effort - ${projectCode}`);
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSaved, setIsSaved] = useState(false);
  
  const [effortData, setEffortData] = useState({
    header: {
      model_dtl_id: '',
      invc_type: 'V',
      unit_rate: '0',
      status: 'ACT',
      modified_dt: null
    },
    employeeEfforts: [
      {
        id: Date.now() + 1,
        emp_id: '',
        effort_dt: '',
        effort_hours: '',
        status: 'ACT',
        isNew: true
      },
      {
        id: Date.now() + 2,
        emp_id: '',
        effort_dt: '',
        effort_hours: '',
        status: 'ACT',
        isNew: true
      }
    ],
    tempLaborEfforts: [
      {
        id: Date.now() + 3,
        temp_labor_name: '',
        category: '',
        effort_dt: '',
        effort_hours: '',
        invc_number: '',
        effort_ot_hours: '',
        hour_rate: 0,
        overtime_rate: 0,
        status: 'ACT',
        isNew: true
      },
      {
        id: Date.now() + 4,
        temp_labor_name: '',
        category: '',
        effort_dt: '',
        effort_hours: '',
        invc_number: '',
        effort_ot_hours: '',
        hour_rate: 0,
        overtime_rate: 0,
        status: 'ACT',
        isNew: true
      }
    ],
    materialCosts: [
      {
        id: Date.now() + 5,
        material_name: '',
        unit_cost: '',
        status: 'ACT',
        isNew: true
      },
      {
        id: Date.now() + 6,
        material_name: '',
        unit_cost: '',
        status: 'ACT',
        isNew: true
      }
    ],
    empHourlyRate: '40',
    isCompleted: false
  });
  
  const [models, setModels] = useState([]);
  // const [employees, setEmployees] = useState([]);  // COMMENTED OUT - not used
  // const [tempLabors, setTempLabors] = useState([]);  // COMMENTED OUT - not used
  const [selectedModelId, setSelectedModelId] = useState('');
  const [selectedEmpRows, setSelectedEmpRows] = useState([]);
  const [selectedTempRows, setSelectedTempRows] = useState([]);
  const [selectedMatRows, setSelectedMatRows] = useState([]);
  const [expandedSections, setExpandedSections] = useState({
    employee: true,
    tempLabor: true,
    material: true
  });
  
  const isEditable = effortData.header.status === 'ACT';
  
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError('');
        
        console.log('Loading data for project:', projectCode);
        
        const modelsResponse = await api.get(`/effort/models/${projectCode}`);
        console.log('Models response:', modelsResponse.data);
        setModels(modelsResponse.data || []);
        
        // COMMENTED OUT - API endpoints disabled
        // const employeesResponse = await api.get('/effort/employees');
        // console.log('Employees response:', employeesResponse.data);
        // setEmployees(employeesResponse.data || []);
        
        // const tempLaborsResponse = await api.get('/effort/temp-labors');
        // console.log('Temp labors response:', tempLaborsResponse.data);
        // setTempLabors(tempLaborsResponse.data || []);
        
        if (modelsResponse.data && modelsResponse.data.length > 0) {
          setSelectedModelId(modelsResponse.data[0].model_dtl_id);
        } else {
          setError('No models found for this project');
        }
        
      } catch (err) {
        console.error('Load data error:', err);
        setError('Failed to load initial data: ' + (err.response?.data?.message || err.message));
      } finally {
        setLoading(false);
      }
    };
    
    if (projectCode) {
      loadData();
    }
  }, [projectCode]);
  
  useEffect(() => {
    if (selectedModelId) {
      loadEffortData(selectedModelId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModelId]);
  
  const loadEffortData = async (modelId, keepSavedState = false) => {
    try {
      const response = await api.get(`/effort/model/${modelId}`);
      console.log('Loaded effort data:', response.data);
      
      // Map backend data to frontend format
      const mappedData = {
        header: response.data.header || {
          model_dtl_id: modelId,
          invc_type: 'V',
          unit_rate: '0',
          status: 'ACT',
          modified_dt: null
        },
        employeeEfforts: response.data.employeeEfforts.length > 0 
          ? response.data.employeeEfforts.map(emp => ({
              id: emp.emp_effort_id,
              emp_id: emp.employee_id,
              effort_dt: emp.effort_dt ? new Date(emp.effort_dt).toISOString().split('T')[0] : '',
              effort_hours: emp.effort_hours || '',
              employee_name: emp.employee_name,
              status: 'ACT',
              isNew: false
            }))
          : [
              {
                id: Date.now() + 1,
                emp_id: '',
                effort_dt: '',
                effort_hours: '',
                status: 'ACT',
                isNew: true
              },
              {
                id: Date.now() + 2,
                emp_id: '',
                effort_dt: '',
                effort_hours: '',
                status: 'ACT',
                isNew: true
              }
            ],
        tempLaborEfforts: response.data.tempLaborEfforts.length > 0 
          ? response.data.tempLaborEfforts.map(temp => ({
              id: temp.temp_effort_id,
              temp_labor_id: temp.temp_labor_id,
              temp_labor_name: temp.resource_name || '', // Resource name goes to Temp Labor column
              category: temp.temp_labor_id, // Category dropdown value
              category_name: temp.temp_labor_name || '', // Category display name (CAR-FLO II)
              effort_dt: temp.effort_dt ? new Date(temp.effort_dt).toISOString().split('T')[0] : '',
              effort_hours: temp.effort_hours || '',
              invc_number: temp.invc_number || '',
              effort_ot_hours: temp.effort_ot_hours || '',
              resource_name: temp.resource_name || '',
              hour_rate: 0,
              overtime_rate: 0,
              status: 'ACT',
              isNew: false
            }))
          : [
              {
                id: Date.now() + 3,
                temp_labor_name: '',
                category: '',
                effort_dt: '',
                effort_hours: '',
                invc_number: '',
                effort_ot_hours: '',
                hour_rate: 0,
                overtime_rate: 0,
                status: 'ACT',
                isNew: true
              },
              {
                id: Date.now() + 4,
                temp_labor_name: '',
                category: '',
                effort_dt: '',
                effort_hours: '',
                invc_number: '',
                effort_ot_hours: '',
                hour_rate: 0,
                overtime_rate: 0,
                status: 'ACT',
                isNew: true
              }
            ],
        materialCosts: response.data.materialCosts.length > 0 
          ? response.data.materialCosts.map(mat => ({
              id: mat.mat_cost_id,
              material_name: mat.mat_name,
              unit_cost: mat.mat_amount || '',
              status: 'ACT',
              isNew: false
            }))
          : [
              {
                id: Date.now() + 5,
                material_name: '',
                unit_cost: '',
                status: 'ACT',
                isNew: true
              },
              {
                id: Date.now() + 6,
                material_name: '',
                unit_cost: '',
                status: 'ACT',
                isNew: true
              }
            ],
        empHourlyRate: response.data.empHourlyRate && response.data.empHourlyRate !== '0' && response.data.empHourlyRate !== '' ? response.data.empHourlyRate : '40',
        isCompleted: response.data.isCompleted || false
      };
      
      setEffortData(mappedData);
      if (!keepSavedState) {
        setIsSaved(false);
      }
      console.log('Mapped effort data:', mappedData);
    } catch (err) {
      console.error('Load effort data error:', err);
      setError('Failed to load effort data: ' + err.message);
    }
  };
  
  const handleModelChange = (event) => {
    setSelectedModelId(event.target.value);
  };
  
  const handleInvoiceTypeChange = (event) => {
    const invoiceType = event.target.value;
    setEffortData(prev => ({
      ...prev,
      header: {
        ...prev.header,
        invc_type: invoiceType,
        unit_rate: invoiceType === 'V' ? '0' : prev.header.unit_rate
      }
    }));
    setIsSaved(false);
  };
  
  const handleUnitCostChange = (event) => {
    setEffortData(prev => ({
      ...prev,
      header: {
        ...prev.header,
        unit_rate: event.target.value
      }
    }));
    setIsSaved(false);
  };
  
  // COMMENTED OUT - not used
  // const handleEmpHourlyRateChange = (event) => {
  //   setEffortData(prev => ({
  //     ...prev,
  //     empHourlyRate: event.target.value
  //   }));
  // };
  
  // COMMENTED OUT - not used
  // const handleEmployeeEffortChange = (index, field, value) => {
  //   setEffortData(prev => ({
  //     ...prev,
  //     employeeEfforts: prev.employeeEfforts.map((effort, i) => 
  //       i === index ? { ...effort, [field]: value } : effort
  //     )
  //   }));
  // };
  
  // COMMENTED OUT - not used
  // const handleTempLaborEffortChange = (index, field, value) => {
  //   setEffortData(prev => ({
  //     ...prev,
  //     tempLaborEfforts: prev.tempLaborEfforts.map((effort, i) => 
  //       i === index ? { ...effort, [field]: value } : effort
  //     )
  //   }));
  // };
  
  const handleMaterialCostChange = (index, field, value) => {
    setEffortData(prev => ({
      ...prev,
      materialCosts: prev.materialCosts.map((material, i) => 
        i === index ? { ...material, [field]: value } : material
      )
    }));
    setIsSaved(false);
  };
  
  const addRow = async (type) => {
    try {
      // Try API call first
      try {
        const response = await api.post(`/effort/add-row/${selectedModelId}/${type}`);
        
        // Backend now returns array of 3 rows
        const newRows = Array.isArray(response.data) ? response.data : [response.data];
        
        if (type === 'employee') {
          const employeeRows = newRows.map(row => ({
            id: row.id,
            emp_id: '',
            effort_dt: '',
            effort_hours: '',
            status: 'ACT',
            isNew: true
          }));
          
          setEffortData(prev => ({
            ...prev,
            employeeEfforts: [...prev.employeeEfforts, ...employeeRows]
          }));
        } else if (type === 'tempLabor') {
          const tempLaborRows = newRows.map(row => ({
            id: row.id,
            temp_labor_name: '',
            category: '',
            effort_dt: '',
            effort_hours: '',
            invc_number: '',
            effort_ot_hours: '',
            hour_rate: 0,
            overtime_rate: 0,
            status: 'ACT',
            isNew: true
          }));
          
          setEffortData(prev => ({
            ...prev,
            tempLaborEfforts: [...prev.tempLaborEfforts, ...tempLaborRows]
          }));
        } else if (type === 'material') {
          const materialRows = newRows.map(row => ({
            id: row.id,
            material_name: '',
            unit_cost: '',
            status: 'ACT',
            isNew: true
          }));
          
          setEffortData(prev => ({
            ...prev,
            materialCosts: [...prev.materialCosts, ...materialRows]
          }));
        }
      } catch (apiError) {
        console.error('API failed, adding rows locally:', apiError);
        
        // API failed, add 2 rows locally with temporary IDs
        const tempIds = [Date.now() + 1, Date.now() + 2];
        
        if (type === 'employee') {
          const employeeRows = tempIds.map(id => ({
            id: id,
            emp_id: '',
            effort_dt: '',
            effort_hours: '',
            status: 'ACT',
            isNew: true
          }));
          
          setEffortData(prev => ({
            ...prev,
            employeeEfforts: [...prev.employeeEfforts, ...employeeRows]
          }));
        } else if (type === 'tempLabor') {
          const tempLaborRows = tempIds.map(id => ({
            id: id,
            temp_labor_name: '',
            category: '',
            effort_dt: '',
            effort_hours: '',
            invc_number: '',
            effort_ot_hours: '',
            hour_rate: 0,
            overtime_rate: 0,
            status: 'ACT',
            isNew: true
          }));
          
          setEffortData(prev => ({
            ...prev,
            tempLaborEfforts: [...prev.tempLaborEfforts, ...tempLaborRows]
          }));
        } else if (type === 'material') {
          const materialRows = tempIds.map(id => ({
            id: id,
            material_name: '',
            unit_cost: '',
            status: 'ACT',
            isNew: true
          }));
          
          setEffortData(prev => ({
            ...prev,
            materialCosts: [...prev.materialCosts, ...materialRows]
          }));
        }
      }
      
      setSuccess('2 rows added successfully');
    } catch (err) {
      console.error('Add row error:', err);
      setError('Failed to add rows: ' + (err.response?.data?.message || err.message));
    }
  };
  
  const deleteRows = async (type) => {
    try {
      let ids = [];
      
      if (type === 'employee') {
        ids = selectedEmpRows;
        setSelectedEmpRows([]);
      } else if (type === 'tempLabor') {
        ids = selectedTempRows;
        setSelectedTempRows([]);
      } else if (type === 'material') {
        ids = selectedMatRows;
        setSelectedMatRows([]);
      }
      
      if (ids.length === 0) {
        setError('Please select rows to delete');
        return;
      }
      
      await api.delete(`/effort/delete-rows/${type}`, { data: { ids } });
      
      if (type === 'employee') {
        setEffortData(prev => ({
          ...prev,
          employeeEfforts: prev.employeeEfforts.filter((_, i) => !ids.includes(i))
        }));
      } else if (type === 'tempLabor') {
        setEffortData(prev => ({
          ...prev,
          tempLaborEfforts: prev.tempLaborEfforts.filter((_, i) => !ids.includes(i))
        }));
      } else if (type === 'material') {
        setEffortData(prev => ({
          ...prev,
          materialCosts: prev.materialCosts.filter((_, i) => !ids.includes(i))
        }));
      }
      
      setSuccess('Rows deleted successfully');
    } catch (err) {
      setError('Failed to delete rows: ' + err.message);
    }
  };
  
  const saveEffort = async () => {
    try {
      setSaving(true);
      setError('');
      
      const unitCost = parseFloat(effortData.header.unit_rate) || 0;
      
      if (unitCost === 0) {
        const filledMaterials = effortData.materialCosts.filter(
          mat => mat.material_name && mat.material_name.trim() !== '' && mat.unit_cost && mat.unit_cost !== ''
        );
        
        if (filledMaterials.length === 0) {
          setError('Please fill Material Name and Material Cost as mandatory.');
          setSaving(false);
          return;
        }
      }
      
      const partiallyFilledRows = effortData.materialCosts.filter(
        mat => (mat.material_name && !mat.unit_cost) || (!mat.material_name && mat.unit_cost)
      );
      
      if (partiallyFilledRows.length > 0) {
        setError('Material Name and Material Cost must both be filled or both be empty.');
        setSaving(false);
        return;
      }
      
      const saveData = {
        modelDtlId: selectedModelId,
        invoiceType: effortData.header.invc_type,
        unitCost: effortData.header.unit_rate,
        empHourlyRate: effortData.empHourlyRate,
        employeeEfforts: effortData.employeeEfforts,
        tempLaborEfforts: effortData.tempLaborEfforts,
        materialCosts: effortData.materialCosts,
        modifiedDate: effortData.header.modified_dt
      };
      
      await api.post('/effort/save', saveData);
      setSuccess('Effort saved successfully');
      setIsSaved(true);
      
      loadEffortData(selectedModelId, true);
      
    } catch (err) {
      setError('Failed to save effort: ' + err.message);
    } finally {
      setSaving(false);
    }
  };
  
  const completeEffort = async () => {
    try {
      // Check project status FIRST
      const statusResponse = await api.get(`/effort/check-wo-status/${projectCode}`);
      
      // Only show popup if project status is "New"
      if (statusResponse.data.isNew) {
        const confirmed = window.confirm(
          `Work Order of Project ${projectCode} is not closed. Do you want to Close the Effort sheet?`
        );
        
        if (!confirmed) {
          return;
        }
      }
      
      // Process completion
      setSaving(true);
      setError('');
      
      await api.post(`/effort/complete/${selectedModelId}`);
      setSuccess('Effort completed successfully');
      
      // Reload models to reflect completion status
      const modelsResponse = await api.get(`/effort/models/${projectCode}`);
      setModels(modelsResponse.data || []);
      
      // Reload effort data
      await loadEffortData(selectedModelId);
      
      // Show success message for 2 seconds before refreshing
      setTimeout(() => {
        window.location.reload();
      }, 2000);
      
    } catch (err) {
      console.error('Complete effort error:', err);
      setError('Failed to complete effort: ' + err.message);
    } finally {
      setSaving(false);
    }
  };
  
  const handleBack = () => {
    navigate('/effort');
  };
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px" role="status" aria-live="polite">
        <Typography>Loading...</Typography>
      </Box>
    );
  }
  
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Effort Management
          </Typography>
          
          {error && <Alert severity="error" role="alert" aria-live="assertive" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" role="status" aria-live="polite" sx={{ mb: 2 }}>{success}</Alert>}
          
          <Box display="flex" alignItems="center" gap={2} flexWrap="nowrap">
            <Typography variant="h6" sx={{ whiteSpace: 'nowrap' }}>
              Project Code: {projectCode}
            </Typography>
            
            <FormControl sx={{ minWidth: 300 }}>
              <InputLabel id="model-select-label">Model</InputLabel>
              <Select
                labelId="model-select-label"
                label="Model"
                value={selectedModelId || ''}
                onChange={handleModelChange}
                disabled={!isEditable || models.length === 0}
                displayEmpty
                MenuProps={{
                  PaperProps: {
                    style: { maxHeight: 400, minWidth: 400 }
                  }
                }}
              >
                {models.length === 0 ? (
                  <MenuItem value="" disabled>
                    No models available
                  </MenuItem>
                ) : (
                  models.map((model) => (
                    <MenuItem key={model.model_dtl_id} value={model.model_dtl_id}>
                      {model.display_name || 
                       (model.from_model && model.to_model 
                        ? `From ${model.from_model} To ${model.to_model}`
                        : model.from_model || `Model ${model.model_dtl_id}`)
                      }
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            
            <Button
              variant="contained"
              startIcon={<SaveIcon aria-hidden="true" />}
              onClick={saveEffort}
              disabled={!isEditable || saving}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Save
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<CompleteIcon aria-hidden="true" />}
              onClick={completeEffort}
              disabled={!isEditable || saving || !isSaved}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Complete Effort
            </Button>
            <Button
              variant="outlined"
              startIcon={<BackIcon aria-hidden="true" />}
              onClick={handleBack}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Back
            </Button>
          </Box>
        </Paper>
        
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <FormLabel component="legend">Invoice Type:</FormLabel>
              <RadioGroup
                row
                value={effortData.header.invc_type}
                onChange={handleInvoiceTypeChange}
              >
                <FormControlLabel 
                  value="F" 
                  control={<Radio />} 
                  label="Fixed Invoice" 
                  disabled={!isEditable}
                />
                <FormControlLabel 
                  value="V" 
                  control={<Radio />} 
                  label="Variable Invoice" 
                  disabled={!isEditable}
                />
              </RadioGroup>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Unit Cost"
                type="number"
                value={effortData.header.unit_rate}
                onChange={handleUnitCostChange}
                disabled={!isEditable || effortData.header.invc_type === 'V'}
                fullWidth
                inputProps={{ step: "0.01", min: "0" }}
              />
            </Grid>
          </Grid>
        </Paper>
        
        
        {/* Sony Employee Effort Section - COMMENTED OUT */}
        {/* <Paper sx={{ p: 3, mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" component="h2">
              Sony Employee Effort
            </Typography>
            <IconButton
              onClick={() => setExpandedSections(prev => ({
                ...prev,
                employee: !prev.employee
              }))}
              aria-expanded={expandedSections.employee}
              aria-label="toggle employee section"
            >
              {expandedSections.employee ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          
          <Collapse in={expandedSections.employee}>
            <Grid container spacing={2} alignItems="center" mb={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Hourly Rate for Sony Employee($)"
                  type="number"
                  value={effortData.empHourlyRate}
                  onChange={handleEmpHourlyRateChange}
                  disabled={!isEditable}
                  fullWidth
                  inputProps={{ step: "0.01", min: "0" }}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <Box display="flex" gap={2}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => addRow('employee')}
                    disabled={!isEditable}
                  >
                    Add Row
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => deleteRows('employee')}
                    disabled={!isEditable || selectedEmpRows.length === 0}
                  >
                    Delete Selected
                  </Button>
                </Box>
              </Grid>
            </Grid>
            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedEmpRows.length > 0 && selectedEmpRows.length < effortData.employeeEfforts.length}
                        checked={effortData.employeeEfforts.length > 0 && selectedEmpRows.length === effortData.employeeEfforts.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedEmpRows(effortData.employeeEfforts.map((_, i) => i));
                          } else {
                            setSelectedEmpRows([]);
                          }
                        }}
                        disabled={!isEditable}
                      />
                    </TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Effort(in Hours)</TableCell>
                    <TableCell>Total Cost</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {effortData.employeeEfforts.map((effort, index) => (
                    <TableRow key={index}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedEmpRows.includes(index)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedEmpRows([...selectedEmpRows, index]);
                            } else {
                              setSelectedEmpRows(selectedEmpRows.filter(i => i !== index));
                            }
                          }}
                          disabled={!isEditable}
                        />
                      </TableCell>
                      <TableCell>
                        <FormControl fullWidth>
                          <Select
                            value={effort.emp_id || ''}
                            onChange={(e) => handleEmployeeEffortChange(index, 'emp_id', e.target.value)}
                            disabled={!isEditable}
                            displayEmpty
                          >
                            <MenuItem value="" disabled>
                              Select Employee
                            </MenuItem>
                            {employees.map((emp) => (
                              <MenuItem key={emp.employee_id} value={emp.employee_id}>
                                {emp.employee_name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="date"
                          value={effort.effort_dt || ''}
                          onChange={(e) => handleEmployeeEffortChange(index, 'effort_dt', e.target.value)}
                          disabled={!isEditable}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          value={effort.effort_hours || ''}
                          onChange={(e) => handleEmployeeEffortChange(index, 'effort_hours', e.target.value)}
                          disabled={!isEditable}
                          inputProps={{ step: "0.1", min: "0" }}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        ${((effort.effort_hours || 0) * effortData.empHourlyRate).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Collapse>
        </Paper> */}
        
        {/* Temp Labor Effort Section - COMMENTED OUT */}
        {/* <Paper sx={{ p: 3, mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" component="h2">
              Temp Labor Effort
            </Typography>
            <IconButton
              onClick={() => setExpandedSections(prev => ({
                ...prev,
                tempLabor: !prev.tempLabor
              }))}
              aria-expanded={expandedSections.tempLabor}
              aria-label="toggle temp labor section"
            >
              {expandedSections.tempLabor ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          
          <Collapse in={expandedSections.tempLabor}>
            <Box display="flex" gap={2} mb={2}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => addRow('tempLabor')}
                disabled={!isEditable}
              >
                Add Row
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => deleteRows('tempLabor')}
                disabled={!isEditable || selectedTempRows.length === 0}
              >
                Delete Selected
              </Button>
            </Box>
            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedTempRows.length > 0 && selectedTempRows.length < effortData.tempLaborEfforts.length}
                        checked={effortData.tempLaborEfforts.length > 0 && selectedTempRows.length === effortData.tempLaborEfforts.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedTempRows(effortData.tempLaborEfforts.map((_, i) => i));
                          } else {
                            setSelectedTempRows([]);
                          }
                        }}
                        disabled={!isEditable}
                      />
                    </TableCell>
                    <TableCell>Temp Labor</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Effort(in Hours)</TableCell>
                    <TableCell>Invoice No</TableCell>
                    <TableCell>OverTime Hours</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {effortData.tempLaborEfforts.map((effort, index) => (
                    <TableRow key={index}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedTempRows.includes(index)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedTempRows([...selectedTempRows, index]);
                            } else {
                              setSelectedTempRows(selectedTempRows.filter(i => i !== index));
                            }
                          }}
                          disabled={!isEditable}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          value={effort.temp_labor_name || ''}
                          onChange={(e) => handleTempLaborEffortChange(index, 'temp_labor_name', e.target.value)}
                          disabled={!isEditable}
                          fullWidth
                          placeholder="Enter Temp Labor Name"
                        />
                      </TableCell>
                      <TableCell>
                        <FormControl fullWidth>
                          <Select
                            value={effort.category || ''}
                            onChange={(e) => handleTempLaborEffortChange(index, 'category', e.target.value)}
                            disabled={!isEditable}
                            displayEmpty
                          >
                            <MenuItem value="" disabled>
                              Select Category
                            </MenuItem>
                            {tempLabors.map((temp) => (
                              <MenuItem key={temp.temp_labor_id} value={temp.temp_labor_id}>
                                {temp.temp_labor_name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="date"
                          value={effort.effort_dt || ''}
                          onChange={(e) => handleTempLaborEffortChange(index, 'effort_dt', e.target.value)}
                          disabled={!isEditable}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          value={effort.effort_hours || ''}
                          onChange={(e) => handleTempLaborEffortChange(index, 'effort_hours', e.target.value)}
                          disabled={!isEditable}
                          inputProps={{ step: "0.1", min: "0" }}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          value={effort.invc_number || ''}
                          onChange={(e) => handleTempLaborEffortChange(index, 'invc_number', e.target.value)}
                          disabled={!isEditable}
                          fullWidth
                          placeholder="Invoice No"
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          value={effort.effort_ot_hours || ''}
                          onChange={(e) => handleTempLaborEffortChange(index, 'effort_ot_hours', e.target.value)}
                          disabled={!isEditable}
                          inputProps={{ step: "0.1", min: "0" }}
                          fullWidth
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Collapse>
        </Paper> */}
        
        {/* Material Cost Section */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" component="h2">
              Material Cost
            </Typography>
            <IconButton
              onClick={() => setExpandedSections(prev => ({
                ...prev,
                material: !prev.material
              }))}
              aria-expanded={expandedSections.material}
              aria-label={expandedSections.material ? "Collapse material cost section" : "Expand material cost section"}
            >
              {expandedSections.material ? <ExpandLessIcon aria-hidden="true" /> : <ExpandMoreIcon aria-hidden="true" />}
            </IconButton>
          </Box>
          
          <Collapse in={expandedSections.material}>
            <Box display="flex" gap={2} mb={2}>
              <Button
                variant="contained"
                startIcon={<AddIcon aria-hidden="true" />}
                onClick={() => addRow('material')}
                disabled={!isEditable}
                aria-label="Add material cost row"
              >
                Add Row
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon aria-hidden="true" />}
                onClick={() => deleteRows('material')}
                disabled={!isEditable || selectedMatRows.length === 0}
                aria-label="Delete selected material cost rows"
              >
                Delete Selected
              </Button>
            </Box>
            
            <TableContainer>
              <Table aria-label="Material cost entries">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" component="th" scope="col">
                      <Checkbox
                        indeterminate={selectedMatRows.length > 0 && selectedMatRows.length < effortData.materialCosts.length}
                        checked={effortData.materialCosts.length > 0 && selectedMatRows.length === effortData.materialCosts.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedMatRows(effortData.materialCosts.map((_, i) => i));
                          } else {
                            setSelectedMatRows([]);
                          }
                        }}
                        disabled={!isEditable}
                        inputProps={{ 'aria-label': 'Select all material rows' }}
                      />
                    </TableCell>
                    <TableCell component="th" scope="col">Material Name</TableCell>
                    <TableCell component="th" scope="col">Material Cost</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {effortData.materialCosts.map((material, index) => (
                    <TableRow key={index}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedMatRows.includes(index)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedMatRows([...selectedMatRows, index]);
                            } else {
                              setSelectedMatRows(selectedMatRows.filter(i => i !== index));
                            }
                          }}
                          disabled={!isEditable}
                          inputProps={{ 'aria-label': `Select material row ${index + 1}` }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          value={material.material_name || ''}
                          onChange={(e) => handleMaterialCostChange(index, 'material_name', e.target.value)}
                          disabled={!isEditable}
                          fullWidth
                          placeholder="Enter material name"
                          aria-label={`Material name, row ${index + 1}`}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          value={material.unit_cost || ''}
                          onChange={(e) => handleMaterialCostChange(index, 'unit_cost', e.target.value)}
                          disabled={!isEditable}
                          inputProps={{ step: "0.01", min: "0", 'aria-label': `Material cost, row ${index + 1}` }}
                          fullWidth
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Collapse>
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};

export default EffortDetail;